import { clsx, type ClassValue } from "clsx";
import { differenceInHours, format, formatDistanceToNowStrict } from "date-fns";
import { twMerge } from "tailwind-merge";
import Toast from "./components/Toast";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function downloadFile(url: string) {
  const link = document.createElement("a");
  link.href = url;
  link.download = "";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function formatCreatedAt(dateString: string, prefix: string | null) {
  const diff = differenceInHours(new Date(), dateString);

  if (diff < 24) {
    const date = formatDistanceToNowStrict(dateString);
    return prefix ? `${prefix} ${date} ago` : `${date} ago`;
  }

  // 1 year
  if (diff < 8760) {
    const date = format(dateString, "MMM d");
    return prefix ? `${prefix} on ${date}` : date;
  }

  const date = format(dateString, "MMM d, yyyy");
  return prefix ? `${prefix} on ${date}` : date;
}

export function formatPrice(
  priceCents: number | null,
  currency: string | null,
) {
  if (priceCents === null) {
    return null;
  }

  const price = priceCents / 100;
  const hasDecimal = price % 1 !== 0;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency ?? undefined,
    minimumFractionDigits: hasDecimal ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(price);
}

export function formatNumber(value: number) {
  if (Math.abs(value) < 10000) {
    return value.toString();
  }

  return new Intl.NumberFormat("en-US").format(value);
}

export function formatFramesToTimestamp(frames: number, fps: number) {
  const fullSeconds = Math.floor(frames / fps);

  const seconds = Math.floor(fullSeconds % 60)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor(fullSeconds / 60)
    .toString()
    .padStart(1, "0");

  return `${minutes}:${seconds}`;
}

export function round(value: number, decimals: number): number {
  const multiplier = Math.pow(10, decimals);
  return Math.floor(value * multiplier) / multiplier;
}

export function getEnv(key: string) {
  const value = import.meta.env[key];

  if (!value) {
    throw new Error(`Environment variable ${key} is not set`);
  }

  return value;
}

export function getFullname(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
) {
  if (!firstName || !lastName) {
    return "";
  }

  return `${firstName} ${lastName}`.trim();
}

export function copyToClipboard(text: string, toastText?: string) {
  if (text && navigator.clipboard) {
    Toast.info(toastText || "Copied to clipboard");
    return navigator.clipboard.writeText(text);
  }

  return false;
}

export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): T {
  let timeoutId: number | null = null;

  return ((...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
      timeoutId = null;
    }, delay) as any;
  }) as T;
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number,
): T {
  let lastCallTime: number | null = null;

  return ((...args: Parameters<T>) => {
    const now = Date.now();

    if (lastCallTime === null || now - lastCallTime >= delay) {
      lastCallTime = now;
      func(...args);
    }
    // Ignore calls happening within the delay period
  }) as T;
}

export const isActiveElementInput = () => {
  const activeElement = document.activeElement;

  if (!activeElement) {
    return false;
  }

  const tagName = activeElement.tagName.toLowerCase();

  return (
    tagName === "input" ||
    tagName === "textarea" ||
    (activeElement instanceof HTMLElement && activeElement.isContentEditable)
  );
};

export function isSafari() {
  return "safari" in window;
}

export const getOpacityPercentage = (opacity: number): number => {
  return opacity / 100;
};

const getLuminance = (hex: string): number => {
  const color = hex.startsWith("#") ? hex.slice(1, 7) : hex.slice(0, 6);
  const r = parseInt(color.slice(0, 2), 16) / 255;
  const g = parseInt(color.slice(2, 4), 16) / 255;
  const b = parseInt(color.slice(4, 6), 16) / 255;

  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

export const needsBorder = (
  hex: string,
  opacity: number,
  threshold = 0.85,
): boolean => {
  const alpha = getOpacityPercentage(opacity);
  const luminance = getLuminance(hex);

  if (alpha < 0.3) {
    return true;
  }

  const blendedLuminance = alpha * luminance + (1 - alpha) * 1;

  return blendedLuminance > threshold;
};

export function formatAudioDuration(durationMs: number): string {
  const durationInSeconds = durationMs / 1000;
  const totalSeconds = Math.ceil(durationInSeconds);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

const languageDisplayNames = new Intl.DisplayNames(["en"], {
  type: "language",
});

export function getLanguageName(code: string): string | null {
  try {
    return languageDisplayNames.of(code) ?? null;
  } catch {
    return null;
  }
}

export function getVoiceActorAvatar(externalId: string | undefined | null) {
  if (!externalId) {
    return "/mesh-gradients/mesh-0.jpg";
  }
  let hash = 0;
  for (let i = 0; i < externalId.length; i++) {
    hash = externalId.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % 72;
  return `/mesh-gradients/mesh-${index}.jpg`;
}

export function parseHtmlToPlainText(html: string) {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.innerText.trim();
}

export const isValidNumber = (str: string): boolean => {
  if (str === "" || str === "-") return false;
  const num = parseFloat(str);
  return !isNaN(num);
};
